/**
 * Sessão expirada — mesmo fluxo do popup de inatividade.
 *
 * Quando um request volta dizendo que a sessão/nonce venceu, mostrar um toast
 * técnico ("A verificação cookie falhou") não ajuda: a pessoa fica na página,
 * repete a ação e o erro volta. Aqui o caso vira o MESMO desfecho do popup
 * "Você ainda está aí?" (assets/js/lb-inactivity.js): modal de login inline
 * (lb-login-modal.js) — a pessoa entra sem sair da página e ela recarrega.
 * Sem o modal no contexto, fallback antigo: aviso curto, contagem regressiva
 * e volta para o login com `redirect_to` da página atual.
 *
 * Detecção (window.lbSession.isExpired):
 * - marcador `data.lb_session === 'expired'` posto pelo PHP
 *   (Labead\Gestao\Usuario\Session_Expired) no erro de nonce do REST;
 * - códigos do core: `rest_cookie_invalid_nonce`, `rest_not_logged_in`;
 * - texto: código LB-GER-002 (ações de gestão) ou a mensagem crua do core,
 *   para qualquer caminho que não passe pelos dois anteriores.
 *
 * Pontos de entrada já ligados: lbform (submits), htmx (navegação da gestão) e
 * lbAlert.toast (qualquer fetch avulso que só saiba mostrar a mensagem).
 *
 * Antes de mandar alguém para o login, confirma que a sessão morreu mesmo:
 * `admin-ajax.php?action=rest-nonce` (ação do core, só para logados) devolve um
 * nonce novo se o cookie ainda vale — é o caso do nonce velho servido por
 * página cacheada, em que basta trocar o nonce e pedir para repetir a ação.
 * Deslogado de verdade, o core responde "0" e aí sim vem o popup.
 *
 * Vanilla JS, sem dependências — precisa funcionar mesmo com a página meio
 * quebrada.
 *
 * @package Labead
 */

(function () {
	'use strict';

	// Segundos até levar para o login sozinho (a pessoa pode ir antes no botão).
	var REDIRECT_S = 10;

	var TEXT_RE = /LB-GER-002|rest_cookie_invalid_nonce|cookie check failed|verifica(?:ç|c)(?:ã|a)o\s+cookie\s+falhou/i;

	// Renovações de nonce aceitas antes de desistir e tratar como sessão morta —
	// evita laço infinito se o nonce novo continuar sendo recusado.
	var MAX_RENEWALS = 3;

	var handled = false;
	var checking = false;
	var renewals = 0;

	// ================================================================
	// URL de reentrada
	// ================================================================

	/**
	 * URL do login com redirect_to da página atual.
	 *
	 * @return {string} URL absoluta ou relativa para onde mandar a pessoa.
	 */
	function loginUrl() {
		var base = (typeof lb_data !== 'undefined' && lb_data.login_url) || '/wp-login.php';
		return base
			+ (base.indexOf('?') === -1 ? '?' : '&')
			+ 'redirect_to=' + encodeURIComponent(window.location.href);
	}

	// ================================================================
	// Detecção
	// ================================================================

	/**
	 * O texto de uma mensagem denuncia sessão vencida?
	 *
	 * @param {string} text - Mensagem (ou HTML) exibida ao usuário.
	 * @return {boolean} true quando é o caso de sessão vencida.
	 */
	function isExpiredText(text) {
		return !!text && TEXT_RE.test(String(text));
	}

	/**
	 * A resposta de um request é de sessão vencida?
	 *
	 * @param {number|string} status  - HTTP status da resposta.
	 * @param {Object|null}   payload - Corpo JSON já parseado (se houver).
	 * @return {boolean} true quando é o caso de sessão vencida.
	 */
	function isExpired(status, payload) {
		var code = parseInt(status, 10);
		if (code !== 401 && code !== 403) {
			return false;
		}

		if (payload) {
			if (payload.data && payload.data.lb_session === 'expired') {
				return true;
			}
			if (payload.code === 'rest_cookie_invalid_nonce' || payload.code === 'rest_not_logged_in') {
				return true;
			}
			if (isExpiredText(payload.message) || isExpiredText(payload.data && payload.data.message)) {
				return true;
			}
		}

		return false;
	}

	// ================================================================
	// Popup (mesmo visual do aviso de inatividade)
	// ================================================================

	function esc(s) {
		var d = document.createElement('div');
		d.textContent = String(s);
		return d.innerHTML;
	}

	/**
	 * Abre o aviso de sessão vencida.
	 *
	 * Com o modal de login inline disponível (lb-login-modal.js), a pessoa
	 * entra ali mesmo e a página recarrega — sem sair de onde estava. Sem o
	 * modal, fallback antigo: contagem regressiva e redirect para o login.
	 *
	 * Idempotente: vários requests falhando juntos abrem um único popup.
	 *
	 * @return {void}
	 */
	function expired() {
		if (handled) {
			return;
		}
		handled = true;

		if (window.lbLoginModal && !window.lbLoginModal.isOpen()) {
			window.lbLoginModal.open({
				title: 'Sessão expirada',
				hint: 'Por segurança, sua sessão foi encerrada. Entre de novo para continuar de onde parou.',
				ctx: 'session',
			});
			return;
		}

		var url = loginUrl();

		var overlay = document.createElement('div');
		overlay.setAttribute('style',
			'position:fixed;inset:0;z-index:100001;background:rgba(0,0,0,.55);'
			+ 'display:flex;align-items:center;justify-content:center;padding:16px;'
		);

		// Superfície do tema (aluno = RL; gestão = theme-*), não branco fixo — modo escuro.
		// Pela área, não por cadeia de fallback: fora do RL e da gestão (ex.: carrinho) os
		// --theme-* também existem e são a cor do menu da escola — lá fica branco.
		var rl = document.body.classList.contains('bb-readylaunch-template');
		var gestao = !rl && document.documentElement.hasAttribute('data-bs-theme');
		var fundo = rl ? 'var(--bb-rl-background-color,#fff)' : (gestao ? 'var(--theme-surface-alt,#fff)' : '#fff');
		var cor = rl ? 'var(--bb-rl-text-color,#111)' : (gestao ? 'var(--theme-on-surface-alt,#111)' : '#111');

		var box = document.createElement('div');
		box.setAttribute('style',
			'background:' + fundo + ';'
			+ 'color:' + cor + ';border-radius:10px;max-width:380px;width:100%;'
			+ 'padding:22px;font:400 14px/1.5 sans-serif;box-shadow:0 10px 40px rgba(0,0,0,.3);text-align:center;'
		);

		box.innerHTML =
			'<h3 style="margin:0 0 6px;font-size:18px;font-weight:700;">' + esc('Sessão expirada') + '</h3>'
			+ '<p style="margin:0 0 14px;color:color-mix(in srgb,currentColor 80%,transparent);">'
			+ esc('Por segurança, sua sessão foi encerrada. Entre de novo para continuar de onde parou.')
			+ '</p>'
			+ '<p data-countdown style="margin:0 0 14px;font:700 22px/1 sans-serif;color:var(--lb-rl-danger-fg,currentColor);"></p>'
			+ '<a href="' + esc(url) + '" data-go'
			+ ' style="display:block;width:100%;padding:11px;border:0;border-radius:6px;cursor:pointer;'
			+ 'background:#1d4ed8;color:#fff;font:700 15px sans-serif;text-decoration:none;box-sizing:border-box;">'
			+ esc('Entrar novamente') + '</a>';

		overlay.appendChild(box);
		document.body.appendChild(overlay);

		var countdownEl = box.querySelector('[data-countdown]');
		var goEl = box.querySelector('[data-go]');
		var left = REDIRECT_S;

		function render() {
			countdownEl.textContent = 'Levando para o login em ' + left + 's…';
		}
		render();

		var interval = setInterval(function () {
			left -= 1;
			if (left <= 0) {
				clearInterval(interval);
				window.location.href = url;
				return;
			}
			render();
		}, 1000);

		if (goEl && goEl.focus) {
			goEl.focus();
		}
	}

	// ================================================================
	// Confirmação: sessão morta mesmo ou só nonce velho?
	// ================================================================

	/**
	 * Endpoint do core que devolve um nonce REST novo (só para logados).
	 *
	 * @return {string} URL do admin-ajax com a ação `rest-nonce`.
	 */
	function nonceEndpoint() {
		var base = (typeof lb_data !== 'undefined' && lb_data.ajax_url) || '/wp-admin/admin-ajax.php';
		return base + (base.indexOf('?') === -1 ? '?' : '&') + 'action=rest-nonce';
	}

	/**
	 * Nonce velho com sessão viva: troca o nonce e pede para repetir a ação.
	 *
	 * Cobre os requests REST (que leem `lb_data.rest_nonce` na hora do envio).
	 * Formulários com `_wpnonce` impresso no HTML não são alcançados por aqui —
	 * daí o "ou atualize a página"; se insistirem em falhar, os próximos ciclos
	 * estouram o MAX_RENEWALS e cai no popup de reentrada.
	 *
	 * @param {string} fresh - Nonce REST novo devolvido pelo core.
	 * @return {void}
	 */
	function renew(fresh) {
		renewals += 1;
		if (typeof lb_data !== 'undefined') {
			lb_data.rest_nonce = fresh;
		}
		if (window.lbAlert) {
			lbAlert.toast({ color: 'w', message: 'Sessão renovada. Tente de novo ou atualize a página.', sound: false });
		}
	}

	/**
	 * Confirma se a sessão acabou antes de levar a pessoa para o login.
	 *
	 * @return {void}
	 */
	function verify() {
		if (handled || checking) {
			return;
		}
		checking = true;

		fetch(nonceEndpoint(), { credentials: 'same-origin', cache: 'no-store' })
			.then(function (r) { return r.text(); })
			.then(function (body) {
				checking = false;
				var fresh = String(body || '').trim();
				// Deslogado: admin-ajax responde "0" (ação só para logados).
				if (renewals < MAX_RENEWALS && /^[A-Za-z0-9]{8,}$/.test(fresh)) {
					renew(fresh);
					return;
				}
				expired();
			})
			.catch(function () {
				checking = false;
				expired();
			});
	}

	/**
	 * Atalho para os pontos de entrada: trata a resposta se for sessão vencida.
	 *
	 * @param {number|string} status  - HTTP status da resposta.
	 * @param {Object|null}   payload - Corpo JSON já parseado (se houver).
	 * @return {boolean} true quando o caso foi assumido (o chamador deve parar aí).
	 */
	function handle(status, payload) {
		if (!isExpired(status, payload)) {
			return false;
		}
		verify();
		return true;
	}

	window.lbSession = {
		expired: expired,
		handle: handle,
		verify: verify,
		isExpired: isExpired,
		isExpiredText: isExpiredText,
		loginUrl: loginUrl,
	};
})();
